import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react"

import { IROH_SERVICES_API_PREFIX } from "@/api/domain"
import { SortDir } from "@/api/routes/staff"
import { Tag } from "@/api/tags"
import type { MetricWindow } from "@/components/nodes/metric-window"
import type {
  AuthToken,
  IrohNodeListPage,
  IrohNodeListRow,
  ProjectIdParam,
} from "@/types"

export { SortDir }

/// The display name the backend gives the auto-created cloud endpoint on every
/// project. See CLOUD_NODE_NAME in svc-model/src/iroh_node.rs.
///
/// Only for display. It is not an identity: `validate_name` accepts the string,
/// so a user endpoint can carry it too. Use {@link isCloudEndpoint} to tell the
/// platform's endpoint apart.
export const CLOUD_NODE_NAME = "cloud node"

/// Whether this is the platform's own endpoint rather than one the user
/// registered. Mirrors `IrohNode::is_cloud_endpoint` on the backend: the token
/// is written by exactly one caller, the cloud-provisioning path.
export function isCloudEndpoint(node: {
  auth_token: AuthToken | null
}): boolean {
  return node.auth_token === "IsCloudEndpoint"
}

export enum IrohNodeSortColumn {
  Name = "name",
  Group = "group",
  LastAccess = "last_access",
  AuthTokenExpiresAt = "auth_token_expires_at",
}

/** Online/offline filter for the endpoints list. */
export type EndpointStatusFilter = "online" | "offline"

// Define a service using a base URL and expected endpoints
export const irohNodesApi = createApi({
  reducerPath: "irohNodesApi",
  tagTypes: [Tag.IrohNodes],
  baseQuery: fetchBaseQuery({
    baseUrl: `${IROH_SERVICES_API_PREFIX}iroh-nodes`,
    mode: "cors",
    credentials: "include",
  }),
  endpoints: (build) => ({
    irohNodesGet: build.query<
      IrohNodeListPage,
      ProjectIdParam & {
        offset: number
        limit: number
        q?: string
        group?: string
        status?: EndpointStatusFilter
        sort_by?: IrohNodeSortColumn
        sort_dir?: SortDir
        /** The window the traffic and connection counts cover. Omit for all time. */
        window?: MetricWindow
      }
    >({
      query: ({
        project_id,
        offset,
        limit,
        q,
        group,
        status,
        sort_by,
        sort_dir,
        window,
      }) => {
        const params = new URLSearchParams()
        params.set("offset", String(offset))
        params.set("limit", String(limit))
        if (q) params.set("q", q)
        if (group) params.set("group", group)
        if (status) params.set("status", status)
        if (sort_by) params.set("sort_by", sort_by)
        if (sort_dir) params.set("sort_dir", sort_dir)
        if (window) params.set("window", window)
        return { url: `${project_id}?${params.toString()}` }
      },
      providesTags: (result, _err, { project_id }) =>
        result
          ? [
              ...result.items.map(({ node_id }) => ({
                type: Tag.IrohNodes,
                id: node_id,
              })),
              { type: Tag.IrohNodes, id: project_id },
            ]
          : [{ type: Tag.IrohNodes, id: project_id }],
    }),
    /** Distinct group names for the project's endpoints (group filter dropdown). */
    irohNodeGroups: build.query<string[], ProjectIdParam>({
      query: ({ project_id }) => `${project_id}/groups`,
      providesTags: (_result, _err, { project_id }) => [
        { type: Tag.IrohNodes, id: project_id },
      ],
    }),
    /** One endpoint, with the same traffic and connection counts the list
     *  carries, so the detail page and the table can't disagree. */
    irohNodeGet: build.query<
      IrohNodeListRow,
      ProjectIdParam & { node_id: string; window?: MetricWindow }
    >({
      query: ({ project_id, node_id, window }) => {
        const params = new URLSearchParams()
        if (window) params.set("window", window)
        return { url: `${project_id}/${node_id}?${params.toString()}` }
      },
      providesTags: (result) =>
        result ? [{ type: Tag.IrohNodes, id: result.node_id }] : [],
    }),
    irohNodeDelete: build.mutation<void, ProjectIdParam & { node_id: string }>({
      query: ({ project_id, node_id }) => ({
        url: `${project_id}/${node_id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _err, { project_id, node_id }) => [
        { type: Tag.IrohNodes, id: node_id },
        { type: Tag.IrohNodes, id: project_id },
      ],
    }),
  }),
})

export const {
  useIrohNodesGetQuery,
  useIrohNodeGroupsQuery,
  useIrohNodeGetQuery,
  useIrohNodeDeleteMutation,
} = irohNodesApi
